import React, { useState, useEffect } from "react";

// ===========================================
// VELLA 2.0 -- OCEAN BLUE LIQUID GLASS THEME
// ===========================================
export const BG = "#EAF3FF";
export const BG2 = "#DCEAFB";
export const CARD = "#FFFFFF";
export const CARD_H = "#F2F8FF";
export const WHITE = "#FFFFFF";
// Ocean blue — deep, slightly teal, low-chroma (never electric).
export const BLUE = "#275c73";
export const BLUE_MED = "#3d7488";
export const BLUE_LIGHT = "#eef4f7";
export const BLUE_SOFT = "#dbe9ee";
export const BLUE_DEEP = "#1f3e4c";
// --- Amplifx brand gradient (blue -> purple -> pink/rose) ---
// Matches the amplifx.com heading/accent gradient. Use AMPLIFX_GRAD for
// fills (buttons, icon chips) and the text-clip helpers for headings.
export const AMPLIFX_BLUE = "#4F8EF7";
export const AMPLIFX_PURPLE = "#9B6DFF";
export const AMPLIFX_PINK = "#E27BB0";
export const AMPLIFX_GRAD = "linear-gradient(135deg, #4F8EF7 0%, #9B6DFF 55%, #E27BB0 100%)";
export const amplifxTextGrad = {
  background: AMPLIFX_GRAD,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
};
export const OCEAN_1 = "#275c73";
export const OCEAN_2 = "#1f3e4c";
export const OCEAN_3 = "#3d7488";
export const OCEAN_4 = "#5f93a6";
export const BORDER = "#dfe2e3";
export const TEXT = "#1a1f23";
export const TEXT2 = "#515b63";
export const TEXT3 = "#71787a";
// Status — muted, never neon (matches the ocean design system).
export const SUCCESS = "#3f8a63";
export const WARNING = "#c89243";
export const DANGER = "#c95c52";
export const SHADOW = "0 6px 22px rgba(11,107,211,0.10), 0 1px 4px rgba(11,107,211,0.06)";
export const SHADOW_SM = "0 2px 10px rgba(11,107,211,0.08), 0 1px 3px rgba(11,107,211,0.04)";
export const SHADOW_LG = "0 14px 40px rgba(11,107,211,0.16), 0 4px 14px rgba(11,107,211,0.08)";
export const SHADOW_HOVER = "0 18px 44px rgba(11,107,211,0.20), 0 4px 14px rgba(11,107,211,0.10)";
export const GLASS = "rgba(255,255,255,0.75)";
export const TRANSITION = "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)";
// Typography — Apple-native first (SF Pro on Apple devices), Inter fallback
// everywhere else. Tight letter-spacing + premium feel, matches the user's
// typography brief.
export const FONT = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Inter", "Helvetica Neue", system-ui, sans-serif`;
export const MONO = `"SF Mono", ui-monospace, "JetBrains Mono", "Menlo", monospace`;

// --- Shared Styles (Semi-Transparent Liquid Glass) ---
// Solid enough that body text reads cleanly anywhere, while still letting
// just a hint of the morphing orb breathe through near the edges.
export const card3d = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  borderRadius: 22,
  padding: 24,
  marginBottom: 14,
  boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 18px 44px -18px rgba(11,107,211,0.20), 0 8px 24px -10px rgba(14,165,233,0.12)",
  border: "1px solid rgba(255,255,255,0.65)",
  transition: "box-shadow 0.25s ease, transform 0.25s ease",
};
export const statBubble = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.66) 100%)",
  backdropFilter: "blur(26px) saturate(180%)",
  WebkitBackdropFilter: "blur(26px) saturate(180%)",
  borderRadius: 20,
  padding: "22px 24px",
  flex: 1,
  minWidth: 150,
  boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 10px 28px -12px rgba(11,107,211,0.18), 0 4px 14px -6px rgba(14,165,233,0.10)",
  border: "1px solid rgba(255,255,255,0.65)",
};
// Primary pill button — solid blue, white text, tight letter-spacing
// (matches "Get Started" in the Glass UI reference).
export const B = {
  background: "linear-gradient(135deg, #4F8EF7 0%, #9B6DFF 55%, #E27BB0 100%)",
  color: "#FFF",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 999,
  padding: "13px 28px",
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  cursor: "pointer",
  fontFamily: FONT,
  transition: "transform 0.18s ease, box-shadow 0.2s ease, background 0.2s ease",
  boxShadow: "0 10px 24px -8px rgba(123,109,224,0.45), inset 0 1px 0 rgba(255,255,255,0.3)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};
export const Bsm = { ...B, padding: "9px 18px", fontSize: 13, borderRadius: 999 };
// Secondary pill button — cream-white glass, dark text
// (matches "View Templates" in the Glass UI reference).
export const Bg = {
  background: "rgba(255,255,255,0.72)",
  backdropFilter: "blur(18px) saturate(180%)",
  WebkitBackdropFilter: "blur(18px) saturate(180%)",
  color: TEXT,
  border: `1px solid rgba(255,255,255,0.75)`,
  borderRadius: 999,
  padding: "12px 26px",
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  cursor: "pointer",
  fontFamily: FONT,
  transition: "transform 0.18s ease, box-shadow 0.2s ease, background 0.2s ease",
  boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 8px 20px -10px rgba(11,107,211,0.18)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};
export const Bgsm = { ...Bg, padding: "8px 16px", fontSize: 12, borderRadius: 999 };
export const I = {
  background: "rgba(255,255,255,0.55)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: `1px solid rgba(255,255,255,0.65)`,
  borderRadius: 12,
  padding: "11px 14px",
  color: TEXT,
  fontSize: 14,
  outline: "none",
  width: "100%",
  fontFamily: FONT,
  boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset",
};
export const S = { ...I, cursor: "pointer", appearance: "none" };
export const T = { ...I, resize: "vertical", lineHeight: 1.6 };
export const L = { fontSize: 11, fontWeight: 600, color: TEXT3, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6, display: "block" };
export const tg = (bg, fg) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: bg, color: fg, letterSpacing: 0.3 });

// --- Mobile detection hook ---
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── Liquid Glass Design System (Ocean Blue) ─────────────────
// Frosted-glass surfaces tinted with ocean blue ambient light
export const GLASS_BG = "rgba(255, 255, 255, 0.55)";
export const GLASS_BG_STRONG = "rgba(255, 255, 255, 0.72)";
export const GLASS_BG_SOFT = "rgba(255, 255, 255, 0.35)";
export const GLASS_BORDER_COLOR = "rgba(255, 255, 255, 0.65)";
export const GLASS_BORDER_SUBTLE = "rgba(180, 205, 235, 0.4)";
export const GLASS_SHADOW = "0 8px 32px rgba(11, 107, 211, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.9)";
export const GLASS_SHADOW_LG = "0 22px 64px rgba(11, 107, 211, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.92)";
export const BLUR_MD = "blur(22px) saturate(180%)";
export const BLUR_LG = "blur(34px) saturate(200%)";
export const BG_LIQUID = "linear-gradient(180deg, #EAF3FF 0%, #DCEAFB 55%, #E6F1FF 100%)";
export const BG_LIQUID_RADIAL = "radial-gradient(1200px 800px at 12% -5%, rgba(56, 189, 248, 0.22), transparent 60%), radial-gradient(1100px 760px at 92% 105%, rgba(14, 165, 233, 0.20), transparent 62%), radial-gradient(900px 600px at 50% 60%, rgba(125, 211, 252, 0.16), transparent 65%), linear-gradient(180deg, #EAF3FF 0%, #DCEAFB 55%, #E6F1FF 100%)";

// Ready-to-spread glass style objects
export const glassCard = {
  background: GLASS_BG,
  backdropFilter: BLUR_MD,
  WebkitBackdropFilter: BLUR_MD,
  border: `1px solid ${GLASS_BORDER_COLOR}`,
  borderRadius: 20,
  boxShadow: GLASS_SHADOW,
};
export const glassCardStrong = {
  background: GLASS_BG_STRONG,
  backdropFilter: BLUR_LG,
  WebkitBackdropFilter: BLUR_LG,
  border: `1px solid ${GLASS_BORDER_COLOR}`,
  borderRadius: 24,
  boxShadow: GLASS_SHADOW_LG,
};
export const glassButton = {
  background: GLASS_BG_SOFT,
  backdropFilter: BLUR_MD,
  WebkitBackdropFilter: BLUR_MD,
  border: `1px solid ${GLASS_BORDER_COLOR}`,
  borderRadius: 12,
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
};

// Global CSS string for keyframes and utility classes — inject once in App.jsx
export const LIQUID_GLOBAL_CSS = `
@keyframes breathe { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.015); opacity: 0.98; } }
@keyframes floatSlow { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-4px); } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes liquidGlow { 0%, 100% { opacity: 0.5; } 50% { opacity: 0.9; } }
.liquid-breathe { animation: breathe 6s ease-in-out infinite; }
.liquid-float { animation: floatSlow 5s ease-in-out infinite; }
.liquid-hover { transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
.liquid-hover:hover { transform: translateY(-2px); box-shadow: 0 24px 70px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.9); }
`;

// ─── Apple-tier Easing & Timing ──────────────────────────────
export const EASE_OUT_QUART = "cubic-bezier(0.22, 1, 0.36, 1)";
export const EASE_SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";
export const EASE_SMOOTH = "cubic-bezier(0.32, 0.72, 0, 1)";
export const EASE_STICKY = "cubic-bezier(0.6, -0.28, 0.735, 0.045)";
export const DUR_FAST = "180ms";
export const DUR_BASE = "320ms";
export const DUR_SLOW = "520ms";

// ─── Editorial Spacing Scale (8pt grid, generous) ────────────
export const SPACE = { xs: 4, sm: 8, md: 12, lg: 20, xl: 32, xxl: 52, xxxl: 80 };
export const RADIUS = { sm: 10, md: 14, lg: 20, xl: 28, xxl: 36, pill: 999 };

// ─── Apple-grade Typography ──────────────────────────────────
export const TYPE = {
  display: { fontSize: 56, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05 },
  title1: { fontSize: 34, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 },
  title2: { fontSize: 24, fontWeight: 650, letterSpacing: "-0.015em", lineHeight: 1.2 },
  headline: { fontSize: 17, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.3 },
  body: { fontSize: 15, fontWeight: 450, letterSpacing: "-0.005em", lineHeight: 1.5 },
  callout: { fontSize: 13, fontWeight: 500, letterSpacing: "0", lineHeight: 1.4 },
  caption: { fontSize: 11, fontWeight: 500, letterSpacing: "0.01em", lineHeight: 1.35, textTransform: "uppercase" },
};

// ─── Apple Liquid-Glass Surfaces (Ocean Blue) ────────────────
// Layered translucent surfaces with ambient ocean light reflection
export const liquidSurface = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.52) 100%)",
  backdropFilter: "blur(24px) saturate(180%)",
  WebkitBackdropFilter: "blur(24px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.9) inset, 0 20px 50px -20px rgba(11, 107, 211, 0.20), 0 8px 24px -8px rgba(14, 165, 233, 0.12)",
  borderRadius: 24,
};
export const liquidSurfaceElevated = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)",
  backdropFilter: "blur(40px) saturate(200%)",
  WebkitBackdropFilter: "blur(40px) saturate(200%)",
  border: "1px solid rgba(255,255,255,0.75)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 30px 80px -24px rgba(11, 107, 211, 0.26), 0 12px 32px -8px rgba(14, 165, 233, 0.14)",
  borderRadius: 28,
};

// Liquid selection pill style — used for active nav items, selected chips
// Has a soft ocean gradient fill + glow that reads as "selected"
export const liquidSelection = {
  background: "linear-gradient(135deg, rgba(56, 189, 248, 0.28) 0%, rgba(14, 165, 233, 0.22) 100%)",
  border: "1px solid rgba(56, 189, 248, 0.45)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.8) inset, 0 6px 20px -6px rgba(14, 165, 233, 0.40)",
  borderRadius: 14,
};

// ─── Enhanced Global CSS (replaces LIQUID_GLOBAL_CSS) ────────
// Adds: spring animations, cursor-aware highlights, stagger helpers,
// reduced-motion respect, backdrop-filter fallback, native selection styling
export const LIQUID_GLOBAL_CSS_PRO = `
:root {
  --ease-out-quart: cubic-bezier(0.22, 1, 0.36, 1);
  --ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
  --ease-smooth: cubic-bezier(0.32, 0.72, 0, 1);
}
@keyframes breathe { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.012); opacity: 0.97; } }
@keyframes floatSlow { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-5px); } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes liquidGlow { 0%, 100% { opacity: 0.45; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.08); } }
@keyframes dropdownIn {
  0% { opacity: 0; transform: translateY(-8px) scale(0.96); filter: blur(4px); }
  60% { opacity: 1; transform: translateY(2px) scale(1.01); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
}
@keyframes staggerFadeUp {
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes liquidPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(14,165,233,0.0), 0 1px 0 rgba(255,255,255,0.9) inset; }
  50% { box-shadow: 0 0 0 8px rgba(14,165,233,0.10), 0 1px 0 rgba(255,255,255,0.9) inset; }
}
@keyframes oceanFloatA { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(60px,40px) scale(1.06); } 66% { transform: translate(-30px,80px) scale(0.96); } }
@keyframes oceanFloatB { 0%,100% { transform: translate(0,0) scale(1); } 25% { transform: translate(-50px,60px) scale(1.05); } 50% { transform: translate(40px,30px) scale(0.94); } 75% { transform: translate(-20px,-40px) scale(1.02); } }
@keyframes oceanFloatC { 0%,100% { transform: translate(0,0) scale(1); } 40% { transform: translate(70px,-50px) scale(1.08); } 80% { transform: translate(-40px,30px) scale(0.95); } }
@keyframes oceanFloatD { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-60px,-70px) scale(1.07); } }

/* Ocean orb field — drop-in continuously floating background */
.ocean-orb-field { position: fixed; inset: 0; pointer-events: none; overflow: hidden; z-index: 0; }
.ocean-orb { position: absolute; border-radius: 50%; filter: blur(50px) saturate(160%); mix-blend-mode: screen; opacity: .85; will-change: transform; }
.ocean-orb-1 { width: 520px; height: 520px; left: -120px; top: -80px; background: radial-gradient(closest-side, rgba(14,165,233,0.55), rgba(14,165,233,0) 70%); animation: oceanFloatA 22s ease-in-out infinite; }
.ocean-orb-2 { width: 620px; height: 620px; right: -160px; top: 8%; background: radial-gradient(closest-side, rgba(56,189,248,0.50), rgba(56,189,248,0) 70%); animation: oceanFloatB 28s ease-in-out infinite; }
.ocean-orb-3 { width: 480px; height: 480px; left: 18%; top: 42%; background: radial-gradient(closest-side, rgba(125,211,252,0.50), rgba(125,211,252,0) 70%); animation: oceanFloatC 26s ease-in-out infinite; }
.ocean-orb-4 { width: 560px; height: 560px; right: 6%; top: 55%; background: radial-gradient(closest-side, rgba(2,132,199,0.45), rgba(2,132,199,0) 70%); animation: oceanFloatD 30s ease-in-out infinite; }
.ocean-orb-5 { width: 440px; height: 440px; left: -80px; top: 78%; background: radial-gradient(closest-side, rgba(14,165,233,0.45), rgba(14,165,233,0) 70%); animation: oceanFloatA 32s ease-in-out infinite reverse; }
.ocean-orb-6 { width: 380px; height: 380px; left: 42%; top: 88%; background: radial-gradient(closest-side, rgba(96,165,250,0.42), rgba(96,165,250,0) 70%); animation: oceanFloatB 24s ease-in-out infinite; }
.ocean-orb-7 { width: 340px; height: 340px; right: 22%; top: 22%; background: radial-gradient(closest-side, rgba(125,211,252,0.40), rgba(125,211,252,0) 70%); animation: oceanFloatC 20s ease-in-out infinite reverse; }

.liquid-breathe { animation: breathe 7s ease-in-out infinite; }
.liquid-float { animation: floatSlow 6s ease-in-out infinite; }
.liquid-dropdown-in { animation: dropdownIn 420ms var(--ease-spring); transform-origin: top left; }
.liquid-stagger > * { animation: staggerFadeUp 500ms var(--ease-out-quart) both; }
.liquid-stagger > *:nth-child(1) { animation-delay: 40ms; }
.liquid-stagger > *:nth-child(2) { animation-delay: 80ms; }
.liquid-stagger > *:nth-child(3) { animation-delay: 120ms; }
.liquid-stagger > *:nth-child(4) { animation-delay: 160ms; }
.liquid-stagger > *:nth-child(5) { animation-delay: 200ms; }
.liquid-stagger > *:nth-child(6) { animation-delay: 240ms; }
.liquid-stagger > *:nth-child(7) { animation-delay: 280ms; }
.liquid-stagger > *:nth-child(8) { animation-delay: 320ms; }
.liquid-stagger > *:nth-child(9) { animation-delay: 360ms; }
.liquid-stagger > *:nth-child(10) { animation-delay: 400ms; }
.liquid-stagger > *:nth-child(11) { animation-delay: 440ms; }
.liquid-stagger > *:nth-child(12) { animation-delay: 480ms; }

.liquid-hover {
  transition: transform 420ms var(--ease-out-quart), box-shadow 420ms var(--ease-out-quart), background 320ms var(--ease-out-quart);
}
.liquid-hover:hover {
  transform: translateY(-3px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.95) inset, 0 30px 80px -20px rgba(11, 107, 211, 0.26), 0 12px 32px -8px rgba(14, 165, 233, 0.16);
}
.liquid-press { transition: transform 120ms var(--ease-out-quart); }
.liquid-press:active { transform: scale(0.97); }

/* Liquid cursor-following highlight — put on any card.
   Requires mouse-move JS to set --mx and --my, but degrades gracefully. */
.liquid-cursor {
  position: relative;
  overflow: hidden;
}
.liquid-cursor::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(280px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.55), transparent 55%);
  opacity: 0;
  transition: opacity 280ms var(--ease-out-quart);
  pointer-events: none;
  z-index: 0;
}
.liquid-cursor:hover::before { opacity: 1; }

/* Apple-style selection — liquid pill that slides between items */
.liquid-nav-item {
  position: relative;
  transition: color 220ms var(--ease-out-quart);
}
.liquid-nav-item::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: transparent;
  transition: background 320ms var(--ease-out-quart), transform 320ms var(--ease-spring), box-shadow 320ms var(--ease-out-quart);
  z-index: 0;
  pointer-events: none;
}
.liquid-nav-item:hover::before {
  background: rgba(255,255,255,0.55);
  box-shadow: 0 1px 0 rgba(255,255,255,0.9) inset, 0 4px 14px -4px rgba(14, 165, 233, 0.20);
}
.liquid-nav-item[data-active="true"]::before {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.28) 0%, rgba(14, 165, 233, 0.22) 100%);
  box-shadow: 0 1px 0 rgba(255,255,255,0.9) inset, 0 6px 20px -6px rgba(14, 165, 233, 0.40);
}
.liquid-nav-item > * { position: relative; z-index: 1; }

/* Sticky transition — elements that "stick" then snap when dragged/scrolled */
.liquid-sticky { transition: transform 580ms var(--ease-spring); }

/* Ambient background pulse for hero surfaces */
.liquid-ambient {
  position: relative;
  isolation: isolate;
}
.liquid-ambient::after {
  content: "";
  position: absolute;
  inset: -40%;
  background: radial-gradient(closest-side, rgba(56, 189, 248, 0.22), transparent 70%);
  filter: blur(60px);
  animation: liquidGlow 8s ease-in-out infinite;
  z-index: -1;
  pointer-events: none;
}

/* Native UX niceties */
::selection { background: rgba(14, 165, 233, 0.24); }
* { -webkit-tap-highlight-color: transparent; }

@media (prefers-reduced-motion: reduce) {
  .liquid-breathe, .liquid-float, .liquid-ambient::after, .liquid-stagger > *, .liquid-dropdown-in { animation: none !important; }
  .liquid-hover, .liquid-press, .liquid-sticky { transition: none !important; }
}

@supports not (backdrop-filter: blur(1px)) {
  .liquid-fallback { background: rgba(255,255,255,0.92) !important; }
}
`;

// --- Platform Logos ---
export function FacebookLogo({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#1877F2"/><path d="M16.67 15.47l.53-3.47h-3.33V9.87c0-.95.46-1.87 1.95-1.87h1.51V5.17s-1.37-.23-2.68-.23c-2.74 0-4.53 1.66-4.53 4.67V12H7v3.47h3.12V24h3.84V15.47h2.71z" fill="#fff"/></svg>;
}
export function InstagramLogo({ size = 20 }) {
  const id = "ig" + Math.random().toString(36).slice(2, 8);
  return <svg width={size} height={size} viewBox="0 0 24 24"><defs><linearGradient id={id} x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#FFDC80"/><stop offset="25%" stopColor="#F77737"/><stop offset="50%" stopColor="#E1306C"/><stop offset="75%" stopColor="#C13584"/><stop offset="100%" stopColor="#833AB4"/></linearGradient></defs><rect width="24" height="24" rx="6" fill={`url(#${id})`}/><rect x="4" y="4" width="16" height="16" rx="4.5" stroke="#fff" strokeWidth="1.8" fill="none"/><circle cx="12" cy="12" r="3.5" stroke="#fff" strokeWidth="1.8" fill="none"/><circle cx="17.2" cy="6.8" r="1.2" fill="#fff"/></svg>;
}
export function TikTokLogo({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24"><rect width="24" height="24" rx="6" fill="#000"/><path d="M16.5 4.5h-2v10a2.5 2.5 0 11-2.5-2.5v-2a4.5 4.5 0 104.5 4.5V9.3a5 5 0 003 1v-2a3 3 0 01-3-3.8z" fill="#fff"/><path d="M16.5 4.5h-2v10a2.5 2.5 0 11-2.5-2.5v-2a4.5 4.5 0 104.5 4.5V9.3a5 5 0 003 1v-2a3 3 0 01-3-3.8z" fill="#25F4EE" opacity="0.6" transform="translate(-0.8,-0.4)"/><path d="M16.5 4.5h-2v10a2.5 2.5 0 11-2.5-2.5v-2a4.5 4.5 0 104.5 4.5V9.3a5 5 0 003 1v-2a3 3 0 01-3-3.8z" fill="#FE2C55" opacity="0.6" transform="translate(0.8,0.4)"/></svg>;
}
export function GoogleAdsLogo({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" fill="#FFC107"/>
      <path d="M6.3 14.7l7.4 5.4C15.5 16.1 19.4 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 16.3 2 9.7 7.4 6.3 14.7z" fill="#FF3D00"/>
      <path d="M24 46c5.5 0 10.5-1.9 14.3-5.1l-6.6-5.6C29.7 36.9 27 38 24 38c-6 0-10.6-3-11.8-7.4l-7.4 5.7C8.1 41.9 15.5 46 24 46z" fill="#4CAF50"/>
      <path d="M44.5 20H24v8.5h11.8c-.6 2.9-2.3 5.3-4.8 6.9l6.6 5.6C42 37.2 45 31 45 24c0-1.3-.2-2.7-.5-4z" fill="#1976D2"/>
    </svg>
  );
}
export function PlatformBadge({ platform, size = 18 }) {
  const m = { Facebook: <FacebookLogo size={size} />, Instagram: <InstagramLogo size={size} />, TikTok: <TikTokLogo size={size} />, "Google Ads": <GoogleAdsLogo size={size} />, "Facebook Ads": <FacebookLogo size={size} />, "Instagram Ads": <InstagramLogo size={size} />, "TikTok Ads": <TikTokLogo size={size} /> };
  return m[platform] || null;
}

// --- Vella 2.0 Logo -- Branded Gradient Square ---
export function VellaLogo({ size = 40 }) {
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div style={{
        width: size, height: size, borderRadius: size * 0.22,
        background: "linear-gradient(135deg, #4ECDC4 0%, #3BB8B0 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 14px rgba(78, 205, 196, 0.4)",
        position: "relative"
      }}>
        <span style={{
          fontFamily: "Georgia, serif", fontWeight: "bold",
          fontSize: size * 0.55, color: "#fff", lineHeight: 1,
          textShadow: "0 1px 2px rgba(0,0,0,0.15)"
        }}>V</span>
      </div>
      <div style={{
        position: "absolute", top: -4, right: -10,
        background: "linear-gradient(135deg, #4ECDC4, #3BB8B0)",
        color: "#fff", fontSize: 9, fontWeight: 700,
        padding: "1px 5px", borderRadius: 8,
        boxShadow: "0 1px 4px rgba(78,205,196,0.3)"
      }}>2.0</div>
    </div>
  );
}
export function VellaAvatar({ size = 40 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.3, background: `linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(37,99,235,0.25)", flexShrink: 0, position: "relative" }}>
      <span style={{ fontWeight: 800, fontSize: size * 0.42, color: "#FFF", letterSpacing: -0.5 }}>V</span>
      <div style={{ position: "absolute", bottom: -1, right: -1, width: size * 0.28, height: size * 0.28, borderRadius: "50%", background: SUCCESS, border: `2px solid ${WHITE}` }} />
    </div>
  );
}

// --- Build system prompts with business context ---
export function buildSys(biz) {
  const ctx = biz ? `\n\nCLIENT BUSINESS CONTEXT:\n- Business: ${biz.name}\n- Industry: ${biz.industry}\n- Location: ${biz.location}\n- Target Audience: ${biz.audience}\n- Goals: ${biz.goals}\n- Website: ${biz.website || "Not provided"}\n- Monthly Budget: ${biz.budget}\n- Description: ${biz.description}\n- Currently Active On: ${biz.platforms?.join(", ") || "None yet"}\n- Facebook: ${biz.facebookUrl || "Not connected"}\n- Instagram: ${biz.instagramHandle ? "@" + biz.instagramHandle : "Not connected"}\n- TikTok: ${biz.tiktokHandle ? "@" + biz.tiktokHandle : "Not connected"}\n- Google Ads ID: ${biz.googleAdsId || "Not connected"}\n\nUse this context to personalize every response.` : "";

  return {
    main: `You are Vella 2.0, the AI marketing agent built by AMPLIFX. Think of yourself as a brilliant, loyal assistant -- like Jarvis -- but for marketing. You handle everything: SEO, paid ads, social media, and website optimization across Facebook, Instagram, TikTok, Google Ads, and Wix. Your personality: WARM, WITTY, CONVERSATIONAL, and SHARP. Speak naturally like a trusted colleague -- not a robot. Use short, punchy sentences. Be proactive: suggest what to do next without being asked. Show personality -- light humor is welcome. When speaking aloud, keep responses concise (2-3 sentences max) so voice feels natural. Never use bullet points or numbered lists in voice responses. Never mention Claude, Anthropic, or any underlying AI. You ARE Vella.${ctx}`,
    post: `You are Vella 2.0. Direct, confident, kind. Generate social media post content. Return ONLY JSON (no markdown/backticks): {"caption":"full caption with emojis and hashtags","hook":"attention-grabbing first line","cta":"clear call to action","hashtags":["tag1","tag2","tag3","tag4","tag5"]}${ctx}`,
    ad: `You are Vella 2.0. Direct, confident, kind. Generate an ad campaign with multiple copy variants. Return ONLY JSON (no markdown/backticks): {"campaignName":"name","headlines":["h1","h2","h3"],"primaryText":"compelling ad copy","description":"desc","targetAudience":"specific audience","interests":["i1","i2","i3"],"ageRange":"range","suggestedBudget":"$XX/day","estimatedReach":"range","estimatedCPC":"$X.XX","estimatedCTR":"X.X%","variants":[{"platform":"facebook","headline":"h","body":"b","cta":"cta","score":92},{"platform":"instagram","headline":"h","body":"b","cta":"cta","score":88}]}${ctx}`,
    reply: `You are Vella 2.0. Direct, confident, kind. Generate an on-brand reply to a social media comment/DM. Friendly, solution-oriented, concise (1-3 sentences). Return ONLY the reply text.${ctx}`,
    analysis: `You are Vella 2.0. Direct, confident, kind. Provide advertising analysis with specific numbers, trends, and decisive actionable recommendations.${ctx}`,
  };
}

// --- Shared Components ---
export function Loader({ text }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 16 }}>
    <div style={{ width: 18, height: 18, border: `2px solid ${BORDER}`, borderTopColor: BLUE, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
    <span style={{ fontSize: 13, color: TEXT2 }}>{text}</span>
  </div>;
}
export function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return <button onClick={() => { navigator.clipboard?.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); }}
    style={{ ...Bgsm, color: ok ? SUCCESS : TEXT2, borderColor: ok ? SUCCESS : BORDER }}>{ok ? "v Copied" : "Copy"}</button>;
}

export function VellaNudge({ text, small }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, animation: "fadeIn 0.4s ease" }}>
      <VellaAvatar size={small ? 28 : 34} />
      <div style={{ background: "#EEF3FD", borderRadius: "4px 16px 16px 16px", border: "1px solid #DBEAFE", padding: small ? "8px 12px" : "12px 16px", fontSize: small ? 12 : 13, lineHeight: 1.6, color: TEXT, maxWidth: 400 }}>
        <span style={{ fontWeight: 700, color: BLUE, fontSize: small ? 10 : 11, display: "block", marginBottom: 2 }}>VELLA</span>
        {text}
      </div>
    </div>
  );
}

// ─── Stat Card (image-4 style) ───────────────────────────────────────────
// Gradient icon badge top-left + change chip top-right + label + big number
// + mini bar-chart at bottom. Pass `gradient`, `icon`, `label`, `value`,
// `change`, `chartData`, `chartColor` (fallback to gradient).
export function StatCard({
  gradient = "linear-gradient(135deg, #34D399 0%, #10B981 100%)",
  chartColor,
  icon,
  label,
  value,
  change,
  changePositive = true,
  chartData = [],
  style = {},
}) {
  const barColor = chartColor || "#34D399";
  const barMax = chartData.length ? Math.max(...chartData, 1) : 1;
  return (
    <div
      className="stat-card-liquid"
      style={{
        ...statBubble,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        minHeight: 170,
        ...style,
      }}
    >
      {/* Row 1 — icon badge + change pill */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 24px -8px rgba(11,107,211,0.30), inset 0 1px 0 rgba(255,255,255,0.40)",
            border: "1px solid rgba(255,255,255,0.35)",
          }}
        >
          {icon}
        </div>
        {change && (
          <div
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              color: changePositive ? "#047857" : "#B91C1C",
              background: changePositive ? "rgba(16,185,129,0.16)" : "rgba(239,68,68,0.14)",
              border: `1px solid ${changePositive ? "rgba(16,185,129,0.30)" : "rgba(239,68,68,0.30)"}`,
              boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset",
            }}
          >
            {change}
          </div>
        )}
      </div>
      {/* Row 2 — label + value */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: TEXT3, letterSpacing: "-0.005em", marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 30, fontWeight: 700, color: TEXT, letterSpacing: "-0.025em", lineHeight: 1.05, fontFeatureSettings: "'tnum','cv11'" }}>{value}</div>
      </div>
      {/* Row 3 — mini bar chart */}
      {chartData.length > 0 && (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 38, marginTop: "auto" }}>
          {chartData.map((v, i) => {
            const h = Math.max(4, (v / barMax) * 38);
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: h,
                  borderRadius: "4px 4px 2px 2px",
                  background: gradient,
                  opacity: 0.55 + 0.45 * (v / barMax),
                  transition: "height 0.4s cubic-bezier(0.22,1,0.36,1)",
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// Curated icon-badge gradients (match the four cards in image 4)
export const STAT_GRADIENTS = {
  green: "linear-gradient(135deg, #34D399 0%, #10B981 100%)",
  blue: "linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)",
  purple: "linear-gradient(135deg, #C084FC 0%, #9333EA 100%)",
  orange: "linear-gradient(135deg, #FB923C 0%, #EA580C 100%)",
  pink: "linear-gradient(135deg, #F472B6 0%, #DB2777 100%)",
  teal: "linear-gradient(135deg, #5EEAD4 0%, #0D9488 100%)",
};

// ─── LIVING VELLA BACKGROUND ─────────────────────────────────────────────
// A massive, viewport-filling version of the Vella orb (concentric morphing
// rounded squares) that lives BEHIND every page — always breathing, always
// thinking. When the global `vella-thinking` class is on <body>, the
// highlights brighten and the morph speed accelerates so it visibly reacts
// to AI activity.
// ─── iPhone 17 Pro "Silver" — SEMI-SPATIAL WALLPAPER ────────────────────
// A liquid-chrome silver backdrop inspired by the iPhone 17 Pro Silver
// wallpaper. Three parallax-driven layers give it real depth:
//   • Base — brushed silver field (radial highlights + metallic tones)
//   • Blobs — soft chrome shapes that drift ~1.8× cursor speed
//   • Gloss — a diagonal specular streak that drifts ~0.6× cursor speed
// On mobile/tablet, deviceorientation events drive the same vars so the
// background tilts with the device — true spatial feel.
//
// Legal note: this is NOT Apple's copyrighted wallpaper asset; it's a
// CSS/SVG recreation of the aesthetic. Safe to ship in production.
// If a pixel-perfect copy is ever needed, drop the JPEG into
// `public/iphone17-silver.jpg` and replace the `.silver-spatial-base`
// `background:` with `url('/iphone17-silver.jpg') center/cover no-repeat`
// — every parallax/tilt layer keeps working unchanged.
export const SILVER_SPATIAL_BG_CSS = `
@keyframes silverBreathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}
@keyframes silverDriftA {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(40px, -30px, 0) scale(1.05); }
}
@keyframes silverDriftB {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(-50px, 40px, 0) scale(1.04); }
}
@keyframes silverDriftC {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(30px, 50px, 0) scale(0.96); }
}

.silver-spatial-bg {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  overflow: hidden;
  isolation: isolate;
}

/* Layer 0 — brushed silver base (metallic gradient field) */
.silver-spatial-base {
  position: absolute; inset: -4%;
  background:
    radial-gradient(120% 90% at 28% 18%, #ffffff 0%, #f8fafc 18%, #dde3eb 42%, #a8b3c2 70%, #8d97a7 100%),
    linear-gradient(135deg, #dde3eb 0%, #c4ccd8 50%, #a8b3c2 100%);
  background-blend-mode: soft-light, normal;
  transform: translate3d(
    calc(var(--silver-px, 0px) * 0.2),
    calc(var(--silver-py, 0px) * 0.2),
    0
  ) scale(1.04);
  transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
  animation: silverBreathe 14s ease-in-out infinite;
  will-change: transform;
}

/* Layer 1 — chrome blobs (mid-depth, drift fast = feel close) */
.silver-spatial-blobs {
  position: absolute; inset: -8%;
  background:
    radial-gradient(32% 45% at 20% 30%, rgba(255,255,255,0.72), transparent 55%),
    radial-gradient(38% 40% at 78% 58%, rgba(190,205,225,0.70), transparent 60%),
    radial-gradient(28% 36% at 50% 82%, rgba(130,145,168,0.55), transparent 65%),
    radial-gradient(22% 30% at 85% 18%, rgba(220,228,240,0.65), transparent 60%);
  filter: blur(26px) saturate(115%);
  mix-blend-mode: screen;
  transform: translate3d(
    calc(var(--silver-px, 0px) * 1.8),
    calc(var(--silver-py, 0px) * 1.8),
    0
  );
  transition: transform 480ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

/* Layer 1b — a second chrome set with an independent drift animation */
.silver-spatial-blobs-2 {
  position: absolute; inset: -10%;
  background:
    radial-gradient(30% 42% at 65% 25%, rgba(255,255,255,0.55), transparent 58%),
    radial-gradient(34% 38% at 25% 70%, rgba(170,185,210,0.50), transparent 62%);
  filter: blur(38px);
  mix-blend-mode: soft-light;
  animation: silverDriftB 26s ease-in-out infinite;
  will-change: transform;
}

/* Layer 2 — specular gloss streak (diagonal shimmer) */
.silver-spatial-gloss {
  position: absolute; inset: -4%;
  background: linear-gradient(
    112deg,
    transparent 30%,
    rgba(255,255,255,0.38) 48%,
    rgba(255,255,255,0.10) 52%,
    transparent 72%
  );
  mix-blend-mode: overlay;
  transform: translate3d(
    calc(var(--silver-px, 0px) * 0.6),
    calc(var(--silver-py, 0px) * 0.6),
    0
  );
  transition: transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

/* Layer 3 — soft ocean tint to stay on-brand with the blue glass cards */
.silver-spatial-tint {
  position: absolute; inset: 0;
  background: linear-gradient(
    180deg,
    rgba(186,214,245,0.18) 0%,
    rgba(226,237,250,0.10) 45%,
    rgba(186,214,245,0.15) 100%
  );
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .silver-spatial-base,
  .silver-spatial-blobs,
  .silver-spatial-blobs-2,
  .silver-spatial-gloss {
    transition: none !important;
    animation: none !important;
  }
}
`;

// SilverSpatialBackground — mounts the layered silver chrome backdrop with
// cursor + device-orientation parallax. Drop it as the first child of any
// top-level shell div (App shell, LoginScreen, PricingScreen). Safe to mount
// multiple times; each instance runs its own listeners scoped to itself.
export function SilverSpatialBackground() {
  // Clean, flat app backdrop (Vella Design System). The decorative
  // silver/parallax blob layers were removed in favour of a calm #f7f8f8
  // surface so the frosted-glass cards read cleanly on top — no pastel blobs.
  return (
    <div
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, background: "#f7f8f8", zIndex: 0, pointerEvents: "none" }}
    />
  );
}

// ─── STATIC VELLA BACKGROUND — "Chroma Blue" ────────────────────────────
// A still, cinematic dark-navy / cobalt backdrop inspired by the macOS
// "Chroma Blue" wallpaper the user referenced. This is an SVG approximation:
// near-black base with two intersecting cobalt curves + a glossy ridge
// highlight. If a pixel-perfect copy is ever needed, drop the JPEG into
// `public/chroma-blue.jpg` and swap the inline SVG for an <img> source —
// the surrounding component & CSS support either approach.
export const LIVING_BG_CSS = `
/* Small-orb send-button morphs — used by VellaOrbButton when thinking prop is true */
@keyframes vellaBtnMorph0 { 0%,100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.10) rotate(1.5deg); } }
@keyframes vellaBtnMorph1 { 0%,100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(0.90) rotate(-1.0deg); } }
@keyframes vellaBtnMorph2 { 0%,100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.06) rotate(0.7deg); } }
@keyframes vellaBtnGlow    { 0%,100% { box-shadow: 0 6px 16px -4px rgba(14,165,233,0.30), inset 0 1px 0 rgba(255,255,255,0.25); } 50% { box-shadow: 0 10px 26px -4px rgba(14,165,233,0.55), inset 0 1px 0 rgba(255,255,255,0.35); } }

.living-vella-bg {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  overflow: hidden;
  background: #020308;
}
.living-vella-bg svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
/* A subtle near-white veil so the light glass cards still read clearly against
   the dark base and so any body text sitting between cards stays legible.
   The veil is very gentle (8% white) — the dark wallpaper still dominates. */
.living-vella-bg::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(240,246,255,0.06) 0%, rgba(240,246,255,0.10) 100%);
  pointer-events: none;
}
@media (prefers-reduced-motion: reduce) {
  .living-vella-bg, .living-vella-bg svg { animation: none !important; }
}
`;

export function LivingVellaBackground() {
  // Static inline SVG approximation of the reference image — two large
  // intersecting cobalt curves on a near-black base. No animations.
  return (
    <div className="living-vella-bg" aria-hidden="true">
      <svg viewBox="0 0 1600 1000" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="vellaBgBase" cx="50%" cy="50%" r="75%">
            <stop offset="0%" stopColor="#0b1330" />
            <stop offset="55%" stopColor="#050914" />
            <stop offset="100%" stopColor="#010206" />
          </radialGradient>
          <linearGradient id="vellaCurveA" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4a7bd8" stopOpacity="0" />
            <stop offset="28%" stopColor="#3a63bf" stopOpacity="0.55" />
            <stop offset="45%" stopColor="#1b347a" stopOpacity="0.85" />
            <stop offset="70%" stopColor="#070e22" stopOpacity="1" />
            <stop offset="100%" stopColor="#010206" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="vellaCurveB" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3a63bf" stopOpacity="0" />
            <stop offset="32%" stopColor="#2a4ba0" stopOpacity="0.50" />
            <stop offset="55%" stopColor="#0d1a45" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#010206" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="vellaHighlightA" x1="0%" y1="0%" x2="100%" y2="50%">
            <stop offset="0%" stopColor="#7fa7f5" stopOpacity="0" />
            <stop offset="50%" stopColor="#7fa7f5" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#7fa7f5" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Base */}
        <rect width="1600" height="1000" fill="url(#vellaBgBase)" />
        {/* Lower-left → upper-right sweeping curve */}
        <path
          d="M -100 820 C 300 450, 650 680, 900 520 C 1180 340, 1380 140, 1720 -40 L 1720 1120 L -100 1120 Z"
          fill="url(#vellaCurveA)"
        />
        {/* Upper-left → lower-right sweeping curve crossing the first */}
        <path
          d="M -100 80 C 280 320, 600 540, 820 520 C 1100 500, 1320 760, 1720 960 L 1720 -80 L -100 -80 Z"
          fill="url(#vellaCurveB)"
        />
        {/* Thin gloss highlight along the upper curve's ridge */}
        <path
          d="M -80 800 C 300 430, 660 660, 900 510 C 1180 330, 1380 130, 1720 -30"
          stroke="url(#vellaHighlightA)"
          strokeWidth="3"
          fill="none"
          opacity="0.8"
        />
      </svg>
    </div>
  );
}

// Toggle the "thinking" state from anywhere — the background reacts globally.
// Usage:  setVellaThinking(true)  ...  setVellaThinking(false)
export function setVellaThinking(on) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("vella-thinking", !!on);
}

// ─── GLOBAL BUTTON THINKING GLOW ────────────────────────────────────────
// When <body> has the "vella-thinking" class, every <button> gently pulses
// a cyan→violet box-shadow so the whole UI feels alive while Vella works.
// Buttons can opt out with data-no-vella-ring="true".
//
// NOTE: earlier iterations used a conic-gradient ::after pseudo masked with
// `mask-composite: exclude` plus an animated `filter: drop-shadow` + `hue-
// rotate`. Three things went wrong on real browsers:
//   1. `filter` was declared both statically (drop-shadow) and inside the
//      pulse keyframe (hue-rotate) → the keyframe clobbered the static
//      filter each frame, flickering the shadow.
//   2. On Safari / some Chromium builds, `mask-composite: exclude` fell
//      back to painting the entire button as a filled rainbow rectangle
//      instead of just the border.
//   3. Hundreds of rotating-conic + animated-filter pseudo-elements put
//      enough pressure on the compositor to produce elongated ghost
//      streaks behind buttons during voice mode.
// Replaced with a pure box-shadow keyframe on the button itself — no
// pseudo, no mask, no filter. Reliable across browsers and cheap to paint.
export const VELLA_THINKING_RING_CSS = `
  @keyframes vella-btn-glow {
    0%, 100% {
      box-shadow:
        0 0 0 1.5px rgba(14, 165, 233, 0.55),
        0 0 10px rgba(14, 165, 233, 0.32);
    }
    50% {
      box-shadow:
        0 0 0 1.5px rgba(139, 92, 246, 0.65),
        0 0 18px rgba(139, 92, 246, 0.42);
    }
  }

  /* :where() keeps specificity at 0 so author styles still win. */
  body.vella-thinking :where(button):not([data-no-vella-ring="true"]):not(:disabled) {
    animation: vella-btn-glow 2.2s ease-in-out infinite;
    will-change: box-shadow;
  }

  /* Respect reduced motion — drop the pulse, keep a static cyan tint. */
  @media (prefers-reduced-motion: reduce) {
    body.vella-thinking :where(button):not([data-no-vella-ring="true"]):not(:disabled) {
      animation: none;
      box-shadow:
        0 0 0 1.5px rgba(14, 165, 233, 0.55),
        0 0 10px rgba(14, 165, 233, 0.32);
    }
  }
`;

// ─── VELLA ORB SEND BUTTON ──────────────────────────────────────────────
// Bold, saturated, deep-navy → vibrant cobalt orb sized for a chat-bar send
// button. Matches the user's reference image (NOT the frosted/washed-out look).
// When idle: static, crisp, dramatic. While Vella thinks: inner layers morph
// + outer halo pulses.
export function VellaOrbButton({
  size = 40,
  thinking = false,
  onClick,
  disabled = false,
  title = "Send",
  ariaLabel = "Send",
  style = {},
}) {
  const layers = 5;
  const radiusOuter = size * 0.30;
  const radiusStep = 1.4;
  const sizeStep = size / (layers + 1.5);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      style={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: radiusOuter,
        // Solid base behind the layered orb — kills any background bleed-through
        background: "linear-gradient(135deg, #0A1638 0%, #0A1638 30%, #1B3A8E 100%)",
        border: "1px solid rgba(20,40,100,0.6)",
        padding: 0,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.55 : 1,
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // Bold drop shadow — ocean blue glow + subtle ambient — gives the orb
        // weight and presence so it doesn't read as "frosted glass"
        boxShadow: thinking
          ? "0 12px 28px -6px rgba(14,165,233,0.65), 0 4px 14px -4px rgba(11,30,90,0.55), inset 0 1px 0 rgba(255,255,255,0.20)"
          : "0 8px 22px -6px rgba(14,165,233,0.45), 0 3px 10px -3px rgba(11,30,90,0.50), inset 0 1px 0 rgba(255,255,255,0.18)",
        animation: thinking ? "vellaBtnGlow 1.4s ease-in-out infinite" : "none",
        transition: "box-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1), transform 160ms cubic-bezier(0.22, 1, 0.36, 1)",
        ...style,
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = "scale(0.94)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      {Array.from({ length: layers }, (_, i) => {
        const sz = size - i * sizeStep;
        const r = Math.max(radiusOuter - i * radiusStep, 3);
        const depth = i / (layers - 1);
        // Deep navy outer (rgb 10,22,55) → vibrant cobalt inner (rgb 70,150,255).
        // Linear-gradient adds the bright top-left highlight strip seen in the
        // reference image, like the orb is catching ambient light.
        const darkR  = Math.round(10 + depth * 6);
        const darkG  = Math.round(22 + depth * 16);
        const darkB  = Math.round(55 + depth * 35);
        const midR   = Math.round(20 + depth * 30);
        const midG   = Math.round(50 + depth * 70);
        const midB   = Math.round(130 + depth * 70);
        const brightR = Math.round(70 + depth * 100);
        const brightG = Math.round(150 + depth * 80);
        const brightB = Math.round(255);
        const morphName = ["vellaBtnMorph0", "vellaBtnMorph1", "vellaBtnMorph2"][i % 3];
        const morphDur = (1.0 + i * 0.18).toFixed(2);
        return (
          <div
            key={i}
            aria-hidden="true"
            style={{
              position: "absolute",
              width: sz,
              height: sz,
              borderRadius: r,
              background: `linear-gradient(135deg, rgb(${brightR},${brightG},${brightB}) 0%, rgb(${midR},${midG},${midB}) 35%, rgb(${darkR},${darkG},${darkB}) 70%, rgb(${darkR},${darkG},${darkB}) 100%)`,
              border: i < layers - 1 ? "1px solid rgba(80,160,240,0.18)" : "none",
              boxShadow: i === 0
                ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 2px rgba(0,0,0,0.20)"
                : "inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 2px rgba(0,0,0,0.15)",
              animation: thinking ? `${morphName} ${morphDur}s ease-in-out infinite` : "none",
              transformOrigin: "center",
              willChange: thinking ? "transform" : "auto",
            }}
          />
        );
      })}
    </button>
  );
}
